import type {
  ChipData,
  Tone,
  RankingEntry,
  PodiumRowData,
} from './bonus-indicators';

const PODIUM_USD = [150, 100, 50] as const;

function endOfWeekIso(): string {
  const now = new Date();
  const day = now.getDay();
  const daysUntilSun = day === 0 ? 0 : 7 - day;
  const sun = new Date(now);
  sun.setDate(now.getDate() + daysUntilSun);
  sun.setHours(23, 59, 59, 999);
  return sun.toISOString();
}

const TITLE = 'Bonus top 3';
const TOOLTIP =
  'Cada semana, los 3 collectors con mas creadores se llevan un bonus extra: el primero gana $150, el segundo $100 y el tercero $50. Por ejemplo, si acabas la semana en segundo lugar recibes $100 aparte de tus pagos normales. La posicion se cierra el domingo por la noche.';

function tone(position: number | null): Tone {
  if (position && position <= 3) return 'success';
  return 'muted';
}

function bottomHint(position: number | null): string | undefined {
  if (position === 1) return 'Mantén el ritmo hasta el domingo.';
  return undefined;
}

function entryExtras(entry: RankingEntry | undefined): Partial<PodiumRowData> {
  if (!entry) return {};
  const extras: Partial<PodiumRowData> = { collectorId: entry.collector_id };
  if (entry.first_name) extras.firstName = entry.first_name;
  if (entry.photo) extras.photo = entry.photo;
  if (entry.collection_id) extras.collectionId = entry.collection_id;
  return extras;
}

function podiumRow(
  pos: number,
  ranking: RankingEntry[],
  userId: string
): PodiumRowData {
  const entry = ranking.find((r) => r.position === pos);
  return {
    position: pos,
    totalLeads: entry ? entry.total_leads : null,
    eur: PODIUM_USD[pos - 1] ?? 0,
    isCurrent: !!entry && entry.collector_id === userId,
    ...entryExtras(entry),
  };
}

function offPodiumRow(
  ranking: RankingEntry[],
  userId: string
): PodiumRowData | null {
  const u = ranking.find((r) => r.collector_id === userId);
  if (!u || u.position <= 3) return null;
  return {
    position: u.position,
    totalLeads: u.total_leads,
    eur: 0,
    isCurrent: true,
    ...entryExtras(u),
  };
}

export function rankingChip(ranking: RankingEntry[], userId: string): ChipData {
  const userPos =
    ranking.find((r) => r.collector_id === userId)?.position ?? null;
  const rows = [1, 2, 3].map((p) => podiumRow(p, ranking, userId));
  const hint = bottomHint(userPos);
  return {
    title: TITLE,
    subtitleCountdownTo: endOfWeekIso(),
    hero: {
      kind: 'podium',
      rows,
      userOffPodium: offPodiumRow(ranking, userId),
    },
    ...(hint && { hint }),
    tooltip: TOOLTIP,
    tone: tone(userPos),
  };
}
